import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { COLORS } from "../../../header/header.styled";
import { fontStack } from "../../account/account-settings.styled";

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative; /* anchor for the dropdown */

  /* Start hidden/off-screen slightly */
  opacity: 0;
  transform: translateX(24px);

  /* When visible, play the animation once */
  &[data-visible="true"] {
    animation: ${slideIn} 260ms ease-out both;
  }

  /* Respect reduced motion */
  @media (prefers-reduced-motion: reduce) {
    &[data-visible="true"] {
      animation: none;
      opacity: 1;
      transform: none;
    }
  }
`;

export const Trigger = styled.button`
  ${fontStack}
  width: 100%;
  text-align: left;
  background: ${COLORS.general};
  color: ${COLORS.textPrimary};
  border: 2px solid ${COLORS.border};
  border-radius: 12px;
  padding: 12px 40px 12px 12px; /* room for chevron */
  font-weight: 600;
  font-size: 15px;
  line-height: 1.2;
  box-sizing: border-box;
  max-height: 44px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  position: relative;

  &:focus {
    outline: none;
    border-color: ${COLORS.focusRing};
    box-shadow: inset 0 0 0 1px ${COLORS.focusRing};
  }

  /* chevron */
  &::after {
    content: "";
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.5 7.5L10 12l4.5-4.5' stroke='%23aab3c5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
  }
`;

export const Placeholder = styled.span`
  ${fontStack}
  color: ${COLORS.textPlaceholder};
  font-weight: 600;
`;

/**
 * Important bits:
 *  - We store the measured max height in a CSS variable (--mh)
 *  - Parent keeps overflow hidden for rounded corners
 *  - Child scroll area becomes a flex item with a *definite* size:
 *      flex: 1; min-height: 0; max-height: var(--mh);
 */
export const Dropdown = styled.div<{ $maxHeight: number }>`
  --mh: ${({ $maxHeight }) => `${$maxHeight}px`};

  position: absolute;
  left: 0;
  right: 0;
  background: ${COLORS.general};
  border: 2px solid ${COLORS.border};
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  overflow: hidden; /* clip rounded corners */
  z-index: 5; /* above panel contents */

  /* Cap the overall dropdown box */
  max-height: var(--mh);

  /* Make internal scroll area take the available space */
  display: flex;
  flex-direction: column;
`;

export const OptionsScroll = styled.div`
  /* This is the key to honoring the cap and enabling scrolling */
  flex: 1;
  min-height: 0; /* required for scrollable flex children */
  max-height: var(--mh);

  overflow-y: auto;
  overscroll-behavior: contain;
`;

export const OptionRow = styled.div<{ $active?: boolean; $selected?: boolean }>`
  ${fontStack}
  display: grid;
  grid-template-columns: 1fr auto; /* text | checkmark */
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin: 0 8px;
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
  border-radius: 12px;
  height: 44px;
  background: ${(
    { $active },
  ) => ($active ? COLORS.neutralOverlay : "transparent")};
  color: ${COLORS.textPrimary};

  &:hover {
    background: ${COLORS.neutralOverlay};
  }

  &[aria-selected="true"] {
    font-weight: 700;
  }
`;

export const Check = styled.span`
  width: 16px;
  height: 16px;
  justify-self: end;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16.7 5.7a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 10.1a1 1 0 011.4-1.4l3.1 3.1 6.8-6.8a1 1 0 011.4 0z' fill='%235E84ED'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;
`;
